<template>
  <section class="service-regions-map">
    <div class="service-regions-map__header mx-auto max-w-4xl px-6 text-center md:px-12">
      <h2 class="text-4xl font-extrabold text-gray-900 md:text-5xl">
        {{ $t('home.serviceRegions.title') }}
      </h2>
      <p class="mt-4 text-lg leading-8 text-slate-600">
        {{ $t('home.serviceRegions.subtitle') }}
      </p>
    </div>

    <div class="service-regions-map__stage">
      <div class="service-regions-map__stage-head">
        <div class="service-regions-map__stage-copy">
          <span>{{ mapCopy.eyebrow }}</span>
          <strong>{{ mapCopy.summary }}</strong>
        </div>

        <dl class="service-regions-map__metrics">
          <div
            v-for="metric in mapMetrics"
            :key="metric.label"
            class="service-regions-map__metric"
          >
            <dt>{{ metric.label }}</dt>
            <dd>{{ metric.value }}</dd>
          </div>
        </dl>
      </div>

      <StaticServiceMap
        class="service-regions-map__map"
        :alt="$t('home.serviceRegions.title')"
        :country-labels="countryLabels"
        :markers="mapMarkers"
        :service-arcs="serviceArcs"
        presentation="immersive"
        :initial-zoom="1"
        :min-zoom="1"
        :max-zoom="1.45"
        :zoom-step="0.1"
      />

      <div class="service-regions-map__region-strip" aria-hidden="true">
        <span
          v-for="region in coverageRegions"
          :key="region.code"
          class="service-regions-map__region-chip"
          :class="{ '': region.isComingSoon }"
        >
          {{ region.label }}
        </span>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import StaticServiceMap from '@/components/StaticServiceMap.vue';
import serviceRegionsData from '@/data/serviceRegions';
import { createServiceMapMarker } from '@/utils/serviceMap';

const { locale } = useI18n();

const regionDisplayOrder = ['CN', 'HK', 'MO', 'TW', 'JP', 'KR', 'AU', 'SG', 'MY', 'PH', 'VN'];
const countryLabelLayout = {
  CN: { coordinates: { lat: 35.2, lng: 105.4 }, dx: -18, dy: -38, anchor: 'end' },
  HK: { coordinates: { lat: 22.3, lng: 114.2 }, dx: -30, dy: -42, anchor: 'end' },
  MO: { coordinates: { lat: 22.2, lng: 113.5 }, dx: 34, dy: 36, anchor: 'start' },
  TW: { coordinates: { lat: 24.3, lng: 121.0 }, dx: 28, dy: -8, anchor: 'start' },
  JP: { coordinates: { lat: 37.4, lng: 138.8 }, dx: 24, dy: -24, anchor: 'start' },
  KR: { coordinates: { lat: 38.1, lng: 127.6 }, dx: -22, dy: -28, anchor: 'end' },
  AU: { coordinates: { lat: -25.4, lng: 134.0 }, dx: 30, dy: -18, anchor: 'start' },
  SG: { coordinates: { lat: 1.3, lng: 103.9 }, dx: 28, dy: 36, anchor: 'start' },
  MY: { coordinates: { lat: 4.2, lng: 102.1 }, dx: -28, dy: 18, anchor: 'end' },
  PH: { coordinates: { lat: 12.9, lng: 122.0 }, dx: 30, dy: 4, anchor: 'start' },
  VN: { coordinates: { lat: 15.8, lng: 108.3 }, dx: -30, dy: -2, anchor: 'end' }
};

const serviceArcPairs = [
  ['CN', 'HK'],
  ['CN', 'MO'],
  ['CN', 'TW'],
  ['CN', 'JP'],
  ['CN', 'KR'],
  ['CN', 'SG'],
  ['SG', 'MY'],
  ['SG', 'PH'],
  ['SG', 'VN'],
  ['SG', 'AU']
];

const localeKey = computed(() => {
  const [baseLocale] = (locale.value || 'zh').split('-');
  return ['zh', 'en', 'ja', 'ko'].includes(baseLocale) ? baseLocale : 'en';
});

const statusCopy = computed(() => {
  switch (localeKey.value) {
    case 'en':
      return {
        active: 'Active',
        comingSoon: 'Coming Soon'
      };
    case 'ja':
      return {
        active: '稼働中',
        comingSoon: '近日公開'
      };
    case 'ko':
      return {
        active: '운영 중',
        comingSoon: '곧 제공 예정'
      };
    default:
      return {
        active: '正常营业',
        comingSoon: '敬请期待'
      };
  }
});

const mapCopy = computed(() => {
  switch (localeKey.value) {
    case 'en':
      return {
        eyebrow: '3D Service Network',
        summary: `Regional service coverage across ${orderedRegions.value.length} service regions`
      };
    case 'ja':
      return {
        eyebrow: '3Dサービスネットワーク',
        summary: `${orderedRegions.value.length}のサービス地域を横断するサービス体制`
      };
    case 'ko':
      return {
        eyebrow: '3D 서비스 네트워크',
        summary: `${orderedRegions.value.length}개 서비스 지역을 연결하는 서비스 커버리지`
      };
    default:
      return {
        eyebrow: '3D 服务网络',
        summary: `覆盖 ${orderedRegions.value.length} 个服务地区的区域服务网络`
      };
  }
});

const getLocalizedText = (value) => {
  if (!value) {
    return '';
  }

  if (typeof value === 'string') {
    return value;
  }

  if (Array.isArray(value)) {
    return value.filter(Boolean).join(' ');
  }

  return value[localeKey.value] ?? value.zh ?? value.en ?? Object.values(value)[0] ?? '';
};

const getLocalizedList = (value) => {
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }

  if (typeof value === 'string') {
    return [value];
  }

  const localizedValue = value[localeKey.value] ?? value.zh ?? value.en ?? [];
  return Array.isArray(localizedValue) ? localizedValue.filter(Boolean) : [localizedValue].filter(Boolean);
};

const orderedRegions = computed(() => {
  return [...serviceRegionsData].sort((left, right) => {
    return regionDisplayOrder.indexOf(left.code) - regionDisplayOrder.indexOf(right.code);
  });
});

const isRegionComingSoon = region => region.offices.every(office => office.isComingSoon);

const activeOfficeCount = computed(() => {
  return orderedRegions.value.reduce((total, region) => {
    return total + region.offices.filter(office => !office.isComingSoon).length;
  }, 0);
});

const officeCount = computed(() => {
  return orderedRegions.value.reduce((total, region) => total + region.offices.length, 0);
});

const mapMetrics = computed(() => {
  const labels = {
    en: ['Service Regions', 'Active Centers', 'Service Nodes'],
    ja: ['サービス地域', '稼働拠点', 'サービスノード'],
    ko: ['서비스 지역', '운영 센터', '서비스 노드'],
    zh: ['服务地区', '已运营中心', '服务节点']
  }[localeKey.value] || ['服务地区', '已运营中心', '服务节点'];

  return [
    { label: labels[0], value: orderedRegions.value.length },
    { label: labels[1], value: activeOfficeCount.value },
    { label: labels[2], value: officeCount.value }
  ];
});

const coverageRegions = computed(() => {
  return orderedRegions.value.map(region => ({
    code: region.code,
    label: getLocalizedText(region.name),
    isComingSoon: isRegionComingSoon(region)
  }));
});

const countryLabels = computed(() => {
  return orderedRegions.value
    .map((region) => {
      const layout = countryLabelLayout[region.code] || {};
      const marker = createServiceMapMarker({
        id: `country-${region.code}`,
        label: getLocalizedText(region.name),
        coordinates: layout.coordinates || region.coordinates,
        labelPreset: {
          dx: layout.dx ?? 16,
          dy: layout.dy ?? -14,
          anchor: layout.anchor || 'start'
        }
      });

      if (!marker) {
        return null;
      }

      return {
        ...marker,
        code: region.code,
        isComingSoon: isRegionComingSoon(region)
      };
    })
    .filter(Boolean);
});

const createArcPath = (from, to) => {
  const dx = to.xPx - from.xPx;
  const dy = to.yPx - from.yPx;
  const distance = Math.hypot(dx, dy);
  const midX = from.xPx + dx / 2;
  const midY = from.yPx + dy / 2 - Math.max(70, distance * 0.18);

  return `M ${from.xPx} ${from.yPx} Q ${midX} ${midY} ${to.xPx} ${to.yPx}`;
};

const serviceArcs = computed(() => {
  const labelByCode = new Map(countryLabels.value.map(country => [country.code, country]));

  return serviceArcPairs
    .map(([fromCode, toCode]) => {
      const from = labelByCode.get(fromCode);
      const to = labelByCode.get(toCode);

      if (!from || !to) {
        return null;
      }

      return {
        id: `${fromCode}-${toCode}`,
        path: createArcPath(from, to),
        isComingSoon: to.isComingSoon
      };
    })
    .filter(Boolean);
});

const mapMarkers = computed(() => {
  return orderedRegions.value.flatMap((region) => {
    return region.offices
      .map((office) => {
        const marker = createServiceMapMarker({
          id: office.id,
          label: '',
          coordinates: office.coordinates,
          presetId: office.id
        });

        if (!marker) {
          return null;
        }

        return {
          ...marker,
          tooltip: {
            regionName: getLocalizedText(region.name),
            officeTitle: getLocalizedText(office.title),
            addressLines: getLocalizedList(office.addressLines),
            email: office.email || region.email || '',
            businessHours: getLocalizedText(office.businessHours || region.businessHours),
            statusLabel: office.isComingSoon ? statusCopy.value.comingSoon : statusCopy.value.active,
            isComingSoon: office.isComingSoon
          }
        };
      })
      .filter(Boolean);
  });
});
</script>

<style scoped>
.service-regions-map {
  padding-top: 5.5rem;
  padding-bottom: 5.5rem;
  background:
    radial-gradient(circle at 50% 4%, rgba(207, 250, 254, 0.48), transparent 34%),
    linear-gradient(180deg, #f8fbff 0%, #ffffff 30%, #f8fafc 100%);
  overflow: hidden;
}

.service-regions-map__header {
  position: relative;
  z-index: 2;
}

.service-regions-map__stage {
  position: relative;
  width: 100%;
  margin-top: clamp(2.25rem, 4vw, 4rem);
  padding: clamp(1.1rem, 2.1vw, 2.25rem) clamp(0.8rem, 2.8vw, 3.5rem) clamp(1.2rem, 2.3vw, 2.4rem);
  background:
    radial-gradient(circle at 12% 20%, rgba(45, 212, 191, 0.24), transparent 28%),
    radial-gradient(circle at 88% 74%, rgba(251, 191, 36, 0.18), transparent 28%),
    linear-gradient(130deg, #061321 0%, #0b2f3b 48%, #12343a 100%);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.12),
    0 42px 90px rgba(15, 23, 42, 0.14);
}

.service-regions-map__stage::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    linear-gradient(90deg, rgba(255, 255, 255, 0.08) 1px, transparent 1px),
    linear-gradient(180deg, rgba(255, 255, 255, 0.06) 1px, transparent 1px);
  background-size: 72px 72px;
  mask-image: linear-gradient(90deg, transparent, #000 12%, #000 88%, transparent);
  opacity: 0.28;
  pointer-events: none;
}

.service-regions-map__stage-head {
  position: relative;
  z-index: 3;
  width: min(100%, 1760px);
  margin: 0 auto clamp(0.9rem, 1.8vw, 1.35rem);
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1.2rem;
}

.service-regions-map__stage-copy {
  display: grid;
  gap: 0.45rem;
  color: #f8fafc;
}

.service-regions-map__stage-copy span {
  color: #67e8f9;
  font-size: 0.78rem;
  font-weight: 900;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.service-regions-map__stage-copy strong {
  max-width: 38rem;
  font-size: clamp(1.1rem, 2vw, 1.72rem);
  line-height: 1.25;
  letter-spacing: 0;
}

.service-regions-map__metrics {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.7rem;
  margin: 0;
}

.service-regions-map__metric {
  min-width: 8.2rem;
  padding: 0.72rem 0.88rem;
  border-radius: 0.7rem;
  background: rgba(8, 47, 73, 0.64);
  box-shadow:
    inset 0 0 0 1px rgba(125, 211, 252, 0.22),
    0 14px 26px rgba(2, 8, 23, 0.16);
  backdrop-filter: blur(10px);
}

.service-regions-map__metric dt {
  color: rgba(224, 242, 254, 0.78);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0;
  white-space: nowrap;
}

.service-regions-map__metric dd {
  margin: 0.28rem 0 0;
  color: #ffffff;
  font-size: clamp(1.35rem, 2.2vw, 2.2rem);
  font-weight: 900;
  line-height: 1;
}

.service-regions-map__map {
  position: relative;
  z-index: 2;
  width: min(100%, 1760px);
  margin: 0 auto;
}

.service-regions-map__region-strip {
  position: relative;
  z-index: 3;
  width: min(100%, 1760px);
  margin: clamp(0.6rem, 1.2vw, 1rem) auto 0;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5rem;
}

.service-regions-map__region-chip {
  display: inline-flex;
  align-items: center;
  min-height: 2rem;
  border-radius: 9999px;
  padding: 0.38rem 0.72rem;
  background: rgba(240, 253, 250, 0.12);
  color: #dffafe;
  font-size: 0.8rem;
  font-weight: 800;
  box-shadow:
    inset 0 0 0 1px rgba(153, 246, 228, 0.18),
    0 10px 20px rgba(2, 8, 23, 0.12);
}

.service-regions-map__region-chip--planned {
  background: rgba(251, 191, 36, 0.12);
  color: #fde68a;
  box-shadow:
    inset 0 0 0 1px rgba(251, 191, 36, 0.2),
    0 10px 20px rgba(2, 8, 23, 0.12);
}

@media (max-width: 767.98px) {
  .service-regions-map {
    padding-top: 4rem;
    padding-bottom: 4rem;
  }

  .service-regions-map__stage {
    margin-top: 2rem;
    padding: 1rem 0.75rem 1.15rem;
  }

  .service-regions-map__stage-head {
    align-items: stretch;
    flex-direction: column;
    gap: 0.85rem;
  }

  .service-regions-map__metrics {
    justify-content: flex-start;
  }

  .service-regions-map__metric {
    min-width: calc(50% - 0.4rem);
    flex: 1 1 calc(50% - 0.4rem);
    padding: 0.62rem 0.72rem;
  }

  .service-regions-map__metric dt {
    font-size: 0.68rem;
  }

  .service-regions-map__region-strip {
    justify-content: flex-start;
    overflow-x: auto;
    flex-wrap: nowrap;
    padding-bottom: 0.2rem;
  }

  .service-regions-map__region-chip {
    flex: 0 0 auto;
    min-height: 1.8rem;
    padding: 0.32rem 0.6rem;
    font-size: 0.72rem;
  }
}
</style>
